import React, { useState, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import styled from 'styled-components';
import { TokenSegment, MAVRK_TOKEN_DISTRIBUTION } from '../../constants/mavrkTokenomics';

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 500px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 400px;
    min-height: 350px;
  }
  
  @media (max-width: 480px) {
    height: 350px;
    min-height: 300px;
  }
`;

const ChartInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  filter: drop-shadow(0 8px 16px rgba(168, 85, 247, 0.3));
  transform: perspective(1000px) rotateX(5deg);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: perspective(1000px) rotateX(0deg) scale(1.02);
  }
`;

const BreakdownCard = styled.div<{ $isVisible: boolean }>`
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, 
    rgba(42, 31, 61, 0.8) 0%, 
    rgba(30, 20, 45, 0.8) 100%
  );
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 16px;
  backdrop-filter: blur(12px);
  opacity: ${props => props.$isVisible ? 1 : 0};
  max-height: ${props => props.$isVisible ? '300px' : '0'};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const BreakdownTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  font-family: 'Inter', sans-serif;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BreakdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
`;

const ColorIndicator = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${props => props.$color};
  box-shadow: 0 0 8px ${props => props.$color}40;
`;

const BreakdownText = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
`;

const BreakdownName = styled.span`
  font-weight: 500;
`;

const BreakdownValue = styled.span`
  font-weight: 600;
  color: #a855f7;
`;

const CustomTooltip = styled.div`
  background: rgba(20, 20, 30, 0.95);
  border: 1px solid rgba(168, 85, 247, 0.4);
  border-radius: 8px;
  padding: 12px 16px;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

const TooltipLabel = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  font-family: 'Inter', sans-serif;
`;

const TooltipValue = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-family: 'Inter', sans-serif;
`;

interface TokenomicsChartProps {
  data: TokenSegment[];
}

const TokenomicsChart: React.FC<TokenomicsChartProps> = ({ data }) => {
  const [activeSegment, setActiveSegment] = useState<string | null>(null);
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
  const [chartSize, setChartSize] = useState({ outerRadius: 180, innerRadius: 80 });

  useEffect(() => {
    const updateChartSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // Mobile
        setChartSize({ outerRadius: Math.min(140, width * 0.35), innerRadius: Math.min(60, width * 0.15) });
      } else if (width < 1024) {
        // Tablet
        setChartSize({ outerRadius: 160, innerRadius: 70 });
      } else {
        // Desktop
        setChartSize({ outerRadius: 180, innerRadius: 80 });
      }
    };

    updateChartSize();
    window.addEventListener('resize', updateChartSize);
    return () => window.removeEventListener('resize', updateChartSize);
  }, []);

  const handleSegmentClick = useCallback((segment: TokenSegment) => {
    if (segment.breakdown && segment.breakdown.length > 0) {
      setActiveSegment(activeSegment === segment.name ? null : segment.name);
    } else {
      setActiveSegment(null);
    }
  }, [activeSegment]);

  const handleMouseEnter = useCallback((segment: TokenSegment) => {
    setHoveredSegment(segment.name);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredSegment(null);
  }, []);

  const renderCustomTooltip = (props: any) => {
    if (props?.active && props?.payload && props.payload.length > 0) {
      const data = props.payload[0];
      return (
        <CustomTooltip>
          <TooltipLabel>{data.name}</TooltipLabel>
          <TooltipValue>{data.value}% of total supply</TooltipValue>
        </CustomTooltip>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: { cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number; name: string }) => {
    if (percent < 0.03) return null; // Don't show labels for very small segments
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#000000"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={14}
        fontWeight={600}
        fontFamily="Inter, sans-serif"
        opacity={hoveredSegment === name ? 1 : 1}
        style={{ transition: 'opacity 0.2s ease' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const activeBreakdown = data.find(segment => segment.name === activeSegment)?.breakdown;

  return (
    <ChartContainer>
      <ChartWrapper>
        <ChartInnerWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={chartSize.outerRadius}
              innerRadius={chartSize.innerRadius}
              fill="#8884d8"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              onClick={(_: any, index: number) => handleSegmentClick(data[index])}
              onMouseEnter={(_: any, index: number) => handleMouseEnter(data[index])}
              onMouseLeave={handleMouseLeave}
              animationDuration={500}
              animationBegin={0}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  style={{
                    filter: hoveredSegment === entry.name 
                      ? `drop-shadow(0 0 12px ${entry.color}) brightness(1.2)`
                      : 'none',
                    cursor: entry.breakdown ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
          </PieChart>
          </ResponsiveContainer>
        </ChartInnerWrapper>
      </ChartWrapper>

      {activeBreakdown && (
        <BreakdownCard $isVisible={true}>
          <BreakdownTitle>{activeSegment} Breakdown</BreakdownTitle>
          <BreakdownList>
            {activeBreakdown.map((item, index) => (
              <BreakdownItem key={index}>
                <ColorIndicator $color={item.color} />
                <BreakdownText>
                  <BreakdownName>{item.name}</BreakdownName>
                  <BreakdownValue>{item.value}%</BreakdownValue>
                </BreakdownText>
              </BreakdownItem>
            ))}
          </BreakdownList>
        </BreakdownCard>
      )}
    </ChartContainer>
  );
};

export default TokenomicsChart;

